"use client";

import { editsShape } from "@/apiUtils/constants";
import { EditsSectionItemType, EditsSectionType } from "@/common/lib/types";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import HeaderVariants from "../common/headerVariants/HeaderVariants";
import PLPHeader from "./PLPHeader";

interface IProps {
  editsSection: EditsSectionType;
  locale: string;
}

interface EditsItem extends EditsSectionItemType {
  active: boolean;
  index: number;
}

const PlpEdits = ({ editsSection, locale }: IProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobileScreen = useMediaQuery("(max-width: 768px)", {
    defaultValue: false,
    initializeWithValue: true,
  });

  const [selectedEdit, setSelectedEdit] = useState<number | null>(null);

  if (!editsSection) return <></>;
  if (
    !editsSection.edits ||
    !editsSection.shape ||
    (editsSection.edits && editsSection.edits.length < 0)
  )
    return <></>;

  const headerHasMedia = editsSection?.edits?.every((v) => Boolean(v.media));

  const handleSelectedEdits = (index: number) => {
    setSelectedEdit(index);
  };

  const constructUrl = useCallback(
    (link: string, index: string, type: string): string => {
      if (!link) {
        return "";
      }
      if (type === "external") {
        return link;
      }
      // Check if the link starts with the locale, if not, prepend it
      let linkIn = link?.startsWith(`/${locale}`) ? link : `/${locale}${link}`;

      // Construct URL object with the modified link
      const url = new URL(`https://-${linkIn}`);
      const hash = url.hash ? url.hash : "";

      // Set the index parameter in the URL search params
      const params = new URLSearchParams(url.search);

      // Return the constructed URL including pathname and search params
      const queryParams = params.toString() ? `?${params.toString()}` : "";
      return `${url.pathname}${queryParams}${hash}`;
    },
    [locale],
  );

  // if(editsSection.edits.length < 6) 
  //   editsSection.edits = editsSection.edits.concat(editsSection.edits.slice(-2))

  const transformedItems: EditsItem[] = useMemo(() => {
    if (editsSection?.edits?.length) {
      const items: EditsItem[] = editsSection?.edits
        ?.filter((item) => item.enable)
        ?.map((item, index) => ({
          ...item,
          active: false,
          index,
          link: {
            ...item.link,
            url:
              item.link?.type === "external"
                ? item?.link?.url
                : constructUrl(item.link?.url, `${index}`, item.link?.type),
          },
        }));
      if (items.length) {
        const queryParams = searchParams.toString() ? `?${searchParams.toString()}` : "";
        const duplicatedItems =
          items?.filter((item) => item.link?.url === pathname + queryParams) ?? [];
        if (duplicatedItems?.length && items?.[duplicatedItems?.[0].index]) {
          if (selectedEdit && items[selectedEdit].link?.url === pathname + queryParams) {
            items[selectedEdit] = { ...items[selectedEdit], active: true };
          } else {
            items[duplicatedItems?.[0].index] = { ...duplicatedItems?.[0], active: true };
          }
        }
      }

      return items;
    }
    return [];
  }, [editsSection?.edits, searchParams, constructUrl, pathname, selectedEdit]);

  if (!headerHasMedia || editsSection.shape === editsShape.COLLAPSED || editsSection.shape == editsShape.CHIP) {
    return (
      <div>
        {/* <HeaderVariants
          variant="withTabs"
          tabs={transformedItems?.map((item) => ({
            label: item?.title,
            href: item?.link?.url,
            index: item?.index,
            active: item?.active,
            selectedEdit: selectedEdit,
            handleSelectedEdits: handleSelectedEdits,
          }))}
        /> */}
        <PLPHeader tabs={
          transformedItems?.map((item) => ({
            src: isMobileScreen ? (item.mobileMedia ?? "") : (item?.media ?? ""),
            alt: item?.title || "",
            label: item?.title || "",
            url: item?.link?.url || "",
            index: item?.index,
            active: item?.active,
            selectedEdit: selectedEdit,
            handleSelectedEdits: handleSelectedEdits,
          })) ?? []

        }
          variant="withTabs"
        />
      </div>
    );
  }

  if (editsSection.shape === editsShape.SQUARE) {
    return (
      <PLPHeader
        variant="withImageGrid"
        tabs={
          transformedItems?.map((item) => ({
            src: isMobileScreen ? (item.mobileMedia ?? "") : (item?.media ?? ""),
            alt: item?.title || "",
            label: item?.title || "",
            url: item?.link?.url || "",
            index: item?.index,
            active: item?.active,
            selectedEdit: selectedEdit,
            handleSelectedEdits: handleSelectedEdits,
          })) ?? []
        }
      />
    );
  }

  if (editsSection.shape === editsShape.ROUND) {
    return (
      <PLPHeader
        variant="withIcons"
        tabs={
          transformedItems?.map((item) => ({
            src: isMobileScreen ? (item.mobileMedia ?? "") : (item?.media ?? ""),
            alt: item?.title,
            label: item?.title,
            url: item.link?.url,
            index: item?.index,
            active: item?.active,
            selectedEdit: selectedEdit,
            handleSelectedEdits: handleSelectedEdits,
          })) ?? []
        }
        background={editsSection.background}
      />
    );
  }
};

export default PlpEdits;